import { readable } from 'svelte/store';

// Measure an element from its own box — never from `window.innerWidth`.
//
// When a page overflows horizontally, a phone zooms out to fit it, and from
// then on `window.innerWidth` reports the zoomed-out *visual* viewport rather
// than the layout viewport (measured: 715 while `documentElement.clientWidth`
// was still 412). Anything sized from that number gets wider, which deepens
// the zoom-out, which grows the number again — a latch that only a reload
// escapes. That is what made the masonry grids blow past the screen after a
// landscape→portrait rotation, and it read to users as "the top bar is
// broken": the sticky bar is one layout-viewport wide, so it ends up covering
// only part of a zoomed-out screen. An element's own box is immune — it tracks
// the layout viewport at any zoom.
//
// usage: <div use:trackWidth={(w) => width = w}>
export function trackWidth(node, set) {
    let notify = set;
    // clientWidth still counts the element's own padding, and the padding here
    // is the grid's side margin — subtract it or every column sits that far
    // past the screen edge.
    const measure = () => {
        const cs = getComputedStyle(node);
        const inset = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
        notify(Math.max(0, node.clientWidth - inset));
    };
    measure(); // synchronous, so the first painted frame already has a real width
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return {
        update(next) { notify = next; measure(); },
        destroy() { ro.disconnect(); },
    };
}

// Tailwind's `md:` breakpoint, readable from JS. Layout decisions that have a
// CSS counterpart must share one source of truth, or the two disagree at the
// boundary — which is how an open mobile search overlay survived a rotation
// into desktop widths and left the top bar with nothing in it.
export const isDesktop = readable(false, (set) => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 768px)');
    set(mq.matches);
    const onChange = (e) => set(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
});
